import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  Collapse,
  IconButton,
  Link,
  Divider
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import PrivacyConsentService, { ConsentData } from '../../services/PrivacyConsentService';

interface ConsentBannerProps {
  onConsentGiven: (consent: ConsentData) => void;
  onClose: () => void;
}

const ConsentBanner: React.FC<ConsentBannerProps> = ({ onConsentGiven, onClose }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [consent, setConsent] = useState({
    analytics: false,
    marketing: false,
    functional: true // Essential cookies always enabled
  });

  const privacyService = PrivacyConsentService;
  const userLocation = privacyService.getUserLocation();
  const compliance = privacyService.getComplianceRequirements();

  const handleAcceptAll = () => {
    const fullConsent = {
      analytics: true,
      marketing: true,
      functional: true
    };
    setConsent(fullConsent);
    privacyService.setConsent(fullConsent);
    onConsentGiven(privacyService.getConsent()!);
    onClose();
  };

  const handleAcceptSelected = () => {
    privacyService.setConsent(consent);
    onConsentGiven(privacyService.getConsent()!);
    onClose();
  };

  const handleRejectAll = () => {
    const minimalConsent = {
      analytics: false,
      marketing: false,
      functional: true // Essential cookies only
    };
    setConsent(minimalConsent);
    privacyService.setConsent(minimalConsent);
    onConsentGiven(privacyService.getConsent()!);
    onClose();
  };

  const handleConsentChange = (type: keyof typeof consent) => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (type === 'functional') return; // Essential cookies cannot be disabled
    setConsent(prev => ({
      ...prev,
      [type]: event.target.checked
    }));
  };

  const getComplianceText = () => {
    if (compliance.requiresGDPR) {
      return {
        title: 'Cookie Consent (GDPR)',
        description: 'We use cookies and similar technologies to provide our services, analyze usage, and show you relevant ads. Under GDPR, we need your consent for non-essential cookies.',
        legalBasis: 'This is required under the General Data Protection Regulation (GDPR).'
      };
    } else if (compliance.requiresCCPA) {
      return {
        title: 'Privacy Notice (CCPA)',
        description: 'We collect and use personal information as described in our Privacy Policy. You have the right to opt-out of the sale of your personal information.',
        legalBasis: 'This notice is required under the California Consumer Privacy Act (CCPA).'
      };
    } else {
      return {
        title: 'Cookie Preferences',
        description: 'We use cookies to improve your experience and show relevant content. You can customize your preferences below.',
        legalBasis: 'We respect your privacy and want to be transparent about our data practices.'
      };
    }
  };

  const complianceText = getComplianceText();

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        maxHeight: '80vh',
        overflow: 'auto',
        borderRadius: '16px 16px 0 0',
        backgroundColor: 'background.paper'
      }}
    >
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
            {complianceText.title}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {complianceText.description}
        </Typography>

        <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: 'block' }}>
          {complianceText.legalBasis}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            onClick={handleAcceptAll}
            sx={{ minWidth: 120 }}
          >
            Accept All
          </Button>
          
          <Button
            variant="outlined"
            onClick={handleRejectAll}
            sx={{ minWidth: 120 }}
          >
            {compliance.requiresGDPR ? 'Reject All' : 'Essential Only'}
          </Button>

          <Button
            variant="text"
            onClick={() => setShowDetails(!showDetails)}
            endIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ minWidth: 120 }}
          >
            Customize
          </Button>
        </Box>

        <Collapse in={showDetails}>
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Cookie Categories
          </Typography>

          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={consent.functional}
                  disabled={true}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Essential Cookies
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Required for basic site functionality. Cannot be disabled.
                  </Typography>
                </Box>
              }
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={consent.analytics}
                  onChange={handleConsentChange('analytics')}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Analytics Cookies
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Help us understand how you use our site to improve performance.
                  </Typography>
                </Box>
              }
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={consent.marketing}
                  onChange={handleConsentChange('marketing')}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Advertising Cookies
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Used to show you relevant ads and measure ad performance.
                  </Typography>
                </Box>
              }
            />
          </Box>

          <Button
            variant="contained"
            onClick={handleAcceptSelected}
            fullWidth
            sx={{ mb: 2 }}
          >
            Save Preferences
          </Button>
        </Collapse>

        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            Learn more in our{' '}
            <Link href="/privacy-policy" target="_blank" rel="noopener">
              Privacy Policy
            </Link>
            {' '}and{' '}
            <Link href="/cookie-policy" target="_blank" rel="noopener">
              Cookie Policy
            </Link>
            {compliance.requiresGDPR && (
              <>
                . You can change your preferences anytime in your account settings.
              </>
            )}
            {compliance.requiresCCPA && (
              <>
                . California residents can{' '}
                <Link href="/ccpa-opt-out" target="_blank" rel="noopener">
                  opt-out of data sale
                </Link>
                .
              </>
            )}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default ConsentBanner;